import _ from "lodash"
import { ImmutableBehaviorSubject as BehaviorSubject } from "data/lib/immutable-behavior-subject"

import Client from "data/_client"
import { ApiResult } from "data/interface/api"
import { push_undo } from 'data/lib/undo'
import { diff_obj } from 'data/lib/diff'
import notificationService from "data/notification"
import { PromiseWatch } from "./lib/promise-helpers"
import validationService from "data/validate"

// private module logic //

export type Organization = {
  organizationid: number,
  organizationdetail: object
} | null

const id: string = "organization"
const initialState: ApiResult<Organization> = { id, state: "init", value: null}
const organizationStream = new BehaviorSubject<ApiResult<Organization>>(initialState)
const organizationEventStream = new BehaviorSubject<{ type: string; payload: any }>({
  type: '',
  payload: {}
})
let orgMap = {}
const _patches_ = PromiseWatch()

function clear(): void {
  organizationStream.next({ id, state: 'ok', value: null })
  orgMap = {}
}

function get(orgMapId: number): Organization {
  return orgMap[orgMapId]
}

function fetch(organizationid: number): Promise<Organization | { message: string }> {
  return Client.get('organization')
  .then(([organization]) => {
    validationService.fetch('organization', organizationid)
    organizationStream.next({ id, state: 'ok', value: organization })
    return organization
  })
  .catch((err: { message: string }) => {
    notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
    return err
  })
}

function patch(organization: Organization): Promise<Organization | { message: string }> {
  const current = organizationStream.getValue().value
  const organizationid = organization?.organizationid
  const changeset = diff_obj(organization, current)
  if (_.isEmpty(changeset)) return new Promise(() => current)

  push_undo({
    undofunc: () => patch(current!),
    redofunc: () => patch(organization)
  })

  // optimistic update
  organizationStream.next({
    id,
    state: 'ok',
    value: organization
  })

  return _patches_.watch(organizationid, Client.patch(`organization?organizationid=eq.${organizationid}`, changeset)
    .then(([organization]) => {
      orgMap[organization.organizationid] = organization
      validationService.fetch('organization', organizationid)
      return organization
    })
    .catch((err: { message: string }) => {
      // on error overwrite optimistic update
      organizationStream.next({ id, state: 'error', error: err, value: current })
      notificationService.notify({ type: 'danger', message: err.message, dismiss: 5000 })
      return err
    }))
}

// public module interface //

const organizationService = {
  stream: () => organizationStream.asObservable(),
  value: () => organizationStream.getValue().value,
  eventStream: () => organizationEventStream.asObservable(),
  clear,
  get,
  fetch,
  patch
}

export default organizationService
